import type { InteractionFamily } from 'Types/ExerciseContentTypes';

/**
 * MUS-456: the exercise-section scene-config binding.
 *
 * Which playable Phaser scene runs an exercise, resolved from the exercise's
 * `interaction_family` (a real content-model fact - `exercise_package.interaction_family`,
 * schema 3.4). This is the exercise section's OWN scene vocabulary, deliberately
 * separate from the tutorial `ExerciseSceneConfig` union in `Types/ExerciseData`:
 * every member of that union extends `IConfig<Objective>` and the tutorial's
 * `InteractiveTemplate1` iterates `config.objectives`, so folding a performed-family
 * OSMD scene into it breaks the tutorial's type inference (established during 7b /
 * MUS-317). The two vocabularies do not share a type.
 *
 * The slice ships one exercise, a performed-family scale read, so `performed`
 * resolves to `OSMDExerciseScene` (the `OSMDPlayableScene` leaf) and the other two
 * families have no playable scene yet. `null` means "not built", not "unknown key" -
 * a caller resolving a family with no scene must not launch anything.
 */

/**
 * The scene keys of the exercise section's playable scenes. A closed set so a
 * resolved key is a real, registered scene key rather than an arbitrary string;
 * grows as new interaction families gain playable scenes.
 */
export type ExercisePlayableSceneKey = 'OSMDExerciseScene';

/**
 * The binding table. A `Record` over the full `InteractionFamily` union rather than
 * a factory / switch, so adding a family to the enum is a compile-time decision here
 * (a missing key fails to type-check) instead of a silent gap discovered at runtime.
 */
export const EXERCISE_SCENE_BINDING: Record<InteractionFamily, ExercisePlayableSceneKey | null> = {
  performed: 'OSMDExerciseScene',
  manipulated: null,
  answered: null,
};

/**
 * Resolve an exercise's interaction family to the playable scene key that runs it,
 * or `null` when the family has no playable scene yet. Callers guard on `null`
 * rather than launching a scene.
 */
export function resolveExerciseSceneKey(
  family: InteractionFamily,
): ExercisePlayableSceneKey | null {
  return EXERCISE_SCENE_BINDING[family];
}
